/**
 * 素材URL解決ユーティリティ
 *
 * 素材の用途（サムネイル表示 / フルサイズ表示 / 動画再生 / D&D送信）に応じて
 * 正しいURLを返す純粋関数群。GCS署名付きURLは引数で外部から注入する。
 */
import type { TemporaryMaterial, CloudMaterial } from './materialsSlice';
import { parseLayerStack } from '../motion/layerStack';
import { isNonRasterOriginal } from '../../components/materials/shared/materialUtils';

// ---------------------------------------------------------------------------
// 定数
// ---------------------------------------------------------------------------
const SUPABASE_STORAGE_BASE =
  'https://lirsarznxaidebqhgxch.supabase.co/storage/v1/object/public';

// ---------------------------------------------------------------------------
// 型定義
// ---------------------------------------------------------------------------
export type AnyMaterial = TemporaryMaterial | CloudMaterial;

/** 解決済みURLセット */
export interface ResolvedMaterialUrls {
  /** グリッド表示・サムネイル用（低解像度優先） */
  thumbnail: string | null;
  /** モーダル表示・フルサイズ表示用（高解像度優先） */
  fullsize: string | null;
  /** 動画再生用（動画素材の場合のみ） */
  video: string | null;
  /** D&D時のsourceUrl（バックエンドAPI送信の参照元） */
  source: string | null;
  /**
   * ラスタ画像しか受け付けない経路 (生成 AI の参考画像・案件画像など) に渡す URL
   * (docs/features/material-raster-rendition-plan.md §3.2)。
   *  - 非ラスタ原本 (SVG): ラスタ版 (raster_url) があればそれ。無ければ null (原本 URL にフォールバックしない。
   *    呼び出し側が `POST .../raster` で取得してから進む: features/materials/ensureRasterAsset.ts)
   *  - ラスタ原本 (PNG/JPEG 等の image/*): fullsize
   *  - 動画・音声: null
   */
  raster: string | null;
  /** GCS署名付きURLが必要かどうか */
  needsSignedUrl: boolean;
  /** temporary素材かどうか */
  isTemporary: boolean;
  /** GCS署名付きURL取得に失敗した場合のエラーメッセージ（成功・未取得時は null） */
  signedUrlError: string | null;
}

// ---------------------------------------------------------------------------
// ヘルパー関数
// ---------------------------------------------------------------------------

/** Supabase Storage の公開URLを構築 */
export function buildStoragePublicUrl(bucket: string, path: string): string {
  return `${SUPABASE_STORAGE_BASE}/${bucket}/${path}`;
}

/** snake_case/camelCase 両方から値を取得 */
function getField(
  material: Record<string, unknown>,
  snakeCase: string,
  camelCase: string,
): string | null {
  return (material[snakeCase] as string) || (material[camelCase] as string) || null;
}

/** GCS素材かどうか判定 */
export function isGcsMaterial(material: AnyMaterial): boolean {
  return (material as Record<string, unknown>).storage_bucket === 'gcs';
}

/** temporary素材かどうか判定 */
export function detectIsTemporary(material: AnyMaterial): boolean {
  if (material.folder_type !== undefined) {
    return material.folder_type === 'temporary';
  }
  return 'temp_path' in material;
}

// ---------------------------------------------------------------------------
// メイン関数
// ---------------------------------------------------------------------------

/**
 * 素材のURLを用途別に解決する。
 *
 * @param material  素材オブジェクト（APIレスポンスそのまま）
 * @param gcsSignedUrl  GCS署名付きURL（外部で非同期取得済みの場合に渡す）
 */
export function resolveMaterialUrls(
  material: AnyMaterial,
  gcsSignedUrl?: string | null,
  signedUrlError?: string | null,
): ResolvedMaterialUrls {
  const m = material as Record<string, unknown>;
  const needsSignedUrl = isGcsMaterial(material);
  const isTemp = detectIsTemporary(material);

  // 各フィールドの正規化
  const thumbnailUrl = getField(m, 'thumbnail_url', 'thumbnailUrl');
  const previewUrl = getField(m, 'preview_url', 'previewUrl');
  const originalUrl = (m.original_url as string) || null;
  const sourceUrl = getField(m, 'source_url', 'sourceUrl');
  const tempPath = (m.temp_path as string) || null;
  const bucket = (m.storage_bucket as string) || null;
  const path = (m.storage_path as string) || null;

  // Supabase Storage 公開URL（非GCSのみ）
  const storagePublicUrl =
    bucket && path && bucket !== 'gcs'
      ? buildStoragePublicUrl(bucket, path)
      : null;

  const contentType = (m.content_type as string) || '';
  const isVideo = contentType.startsWith('video/');
  const isAudio = contentType.startsWith('audio/');
  const isNonRaster = isNonRasterOriginal(contentType);
  const rasterUrl = (m.raster_url as string) || null;

  // --- 用途別の解決 ---

  // thumbnail: (GCS: signedUrl > サムネイル > プレビュー) > storagePublicUrl > temp_path
  const thumbnail = needsSignedUrl
    ? thumbnailUrl || gcsSignedUrl || previewUrl || null
    : thumbnailUrl || previewUrl || storagePublicUrl || tempPath || null;

  // fullsize: (GCS: signedUrl > プレビュー) > storagePublicUrl > original > source > temp_path
  const fullsize = needsSignedUrl
    ? gcsSignedUrl || previewUrl || originalUrl || sourceUrl || null
    : previewUrl || storagePublicUrl || originalUrl || sourceUrl || tempPath || thumbnailUrl || null;

  // video: GCS→signedUrl, 非GCS→storagePublicUrl
  const video = isVideo
    ? (needsSignedUrl
        ? gcsSignedUrl || null
        : storagePublicUrl || previewUrl || tempPath || null)
    : null;

  // source (D&D用):
  // - 画像: (GCS: signedUrl > previewUrl) > storagePublicUrl > original > temp_path > thumbnail
  // - 動画: 再生用 (video) と同じ解決。preview_url / thumbnail_url は動画素材では
  //   サムネイルJPEGを指すため、フォールバックに使うと参照動画 (video-to-video) に
  //   画像URLが流れてしまう。GCS署名URL解決前は null を返し、呼び出し側で弾く。
  // - 音声: 動画と同じ厳格解決 (参照音声 reference_audios に画像系フォールバックURLを
  //   流さない)。GCS署名URL解決前は null を返し、呼び出し側で弾く。
  const source = isVideo
    ? video
    : isAudio
    ? (needsSignedUrl
        ? gcsSignedUrl || null
        : storagePublicUrl || tempPath || null)
    : (needsSignedUrl ? (gcsSignedUrl || previewUrl) : (previewUrl || storagePublicUrl)) ||
      originalUrl ||
      sourceUrl ||
      tempPath ||
      thumbnailUrl ||
      null;

  // raster: 非ラスタ原本はラスタ版のみ (無ければ null)。ラスタ原本の画像は fullsize。
  const raster = isNonRaster
    ? rasterUrl
    : contentType.startsWith('image/')
    ? fullsize
    : null;

  return {
    thumbnail,
    fullsize,
    video,
    source,
    raster,
    needsSignedUrl,
    isTemporary: isTemp,
    // 署名付きURLが必要な素材で取得に失敗した場合のみエラーを伝播
    signedUrlError: needsSignedUrl ? (signedUrlError ?? null) : null,
  };
}

// ---------------------------------------------------------------------------
// dragData 構築
// ---------------------------------------------------------------------------

/**
 * D&D用の dragData オブジェクトを構築する。
 *
 * SimpleMaterialsList の onDragStart で使用し、
 * ScenarioGenerator の各ドロップハンドラが消費する。
 */
/**
 * AI (シナリオ生成・フレーム生成など、backend が画像を取得して LLM に添付する経路) に渡す画像 URL。
 * 非ラスタ原本 (SVG) はラスタ版 (PNG) を優先する。ラスタ版が未生成なら原本の URL を返す
 * (backend の fetch_llm_image_part が添付時に PNG へ描画するので送っても落ちない。以前は原本の SVG が
 * そのまま Claude へ渡り、media_type 不正の 400 でシナリオ生成全体が失敗していた・2026-10-29)。
 * ラスタ原本の画像は原本 (source) のまま。
 */
export function resolveAiSourceUrl(material: AnyMaterial, urls: ResolvedMaterialUrls): string {
  const contentType = (material as Record<string, unknown>).content_type as string | undefined;
  if (isNonRasterOriginal(contentType)) return urls.raster || urls.source || '';
  return urls.source || '';
}

export function buildDragData(
  material: AnyMaterial,
  urls: ResolvedMaterialUrls,
): Record<string, unknown> {
  const m = material as Record<string, unknown>;
  // aspect_ratio はテーブル列に無く generation_metadata に入る。列優先 → メタデータの順で解決。
  const genMeta = m.generation_metadata as {
    has_audio?: boolean; generate_audio?: boolean; aspect_ratio?: string;
    narration_text?: string; song_title?: string; layer_stack?: unknown;
  } | null | undefined;
  const isAudioMaterial = ((m.content_type as string) || '').startsWith('audio/');
  // 非ラスタ原本 (SVG) はラスタ版の URL だけを sourceUrl にする。未生成なら空のまま渡し、
  // 受け手 (addAsset の手前) が ensureRasterAsset で取得する。原本 URL は AI に流さない。
  const sourceUrl = isNonRasterOriginal(m.content_type as string) ? (urls.raster || '') : (urls.source || '');
  const aspectRatio = (m.aspect_ratio as string) || genMeta?.aspect_ratio || null;
  // 動画素材の音声トラック有無。AI 生成素材は has_audio (実際の有無) を優先し、
  // 無ければ generate_audio (生成時の指定) で代替。どちらも無い素材は null。
  const hasAudio = typeof genMeta?.has_audio === 'boolean'
    ? genMeta.has_audio
    : (typeof genMeta?.generate_audio === 'boolean' ? genMeta.generate_audio : null);

  return {
    id: m.id,
    // 素材名 (設定欄の「レイヤー素材」行・解析前の案内に出す)
    ...(typeof m.name === 'string' && m.name.trim() ? { name: m.name } : {}),
    thumbnailUrl: urls.thumbnail || '',
    type: urls.isTemporary ? 'temporary' : 'cloud',
    sourceUrl,
    ...(urls.isTemporary
      ? {}
      : {
          storageBucket: m.storage_bucket,
          storagePath: m.storage_path,
        }),
    contentType: m.content_type,
    asin: m.asin,
    hasProductLinks: (m.has_product_links as boolean) ?? !!(m.asin as string),
    width: m.width,
    height: m.height,
    aspectRatio,
    durationSeconds: m.duration_seconds,
    hasAudio,
    // 音声素材のみ: 選択素材チップの波形プレビューに出す表示テキスト
    // (素材パネルの narrationText と同じ解決順)
    audioText: isAudioMaterial
      ? (genMeta?.narration_text || genMeta?.song_title || null)
      : null,
    // 参照音声の用途の既定値推定 (docs/features/reference-audio-roles-plan.md §2.5) と、
    // 生成フレーム (source_type='scenario') の識別 (assetMediaUtils.isGeneratedSceneFrameAsset)
    // に使う。
    sourceType: (m.source_type as string) ?? null,
    // レイヤー素材 (SVG を解析した合成 PNG) の LayerStack。形を検証して不正なら null
    // (docs/features/motion-graphics-mode-plan.md §3.3・§3.5)。通常の素材ではキーごと付けない
    ...(genMeta?.layer_stack !== undefined
      ? { layerStack: parseLayerStack(genMeta.layer_stack) }
      : {}),
    ...(isAudioMaterial
      ? {
          defaultAudioRole: (m as { default_audio_role?: string | null }).default_audio_role ?? null,
        }
      : {}),
  };
}
